import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Download, Printer, Wrench, PackageOpen, AlertTriangle } from "lucide-react";

const KINDS = ["Service", "Spare parts", "Consumables", "Repair", "Call-out"];

const kindBadge = (k) =>
  k === "Spare parts"
    ? "bg-[#E8F2FF] text-[#0A3A7A] border-[#0A3A7A]"
    : k === "Consumables"
      ? "bg-[#E6F4EA] text-[#1F6F3E] border-[#1F6F3E]"
      : k === "Repair"
        ? "bg-[#FCE4E4] text-[#8A1F1F] border-[#8A1F1F]"
        : k === "Call-out"
          ? "bg-[#FEE0CB] text-[#7A2E00] border-[#7A2E00]"
          : "bg-[#FFF7DB] text-[#5B4500] border-[#7A4A00]";

const kindIcon = (k) =>
  k === "Spare parts"
    ? PackageOpen
    : k === "Consumables"
      ? PackageOpen
      : k === "Repair"
        ? AlertTriangle
        : k === "Call-out"
          ? AlertTriangle
          : Wrench;

const fmt = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const csvEscape = (v) => {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadBlob = (content, filename, mime) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// Open a hidden new window with a print-friendly HTML report and trigger
// print. Kept in the frontend so we don't need a server-side PDF renderer.
export const printReport = (title, subtitle, tableHeaders, tableRows, footer) => {
  const win = window.open("", "_blank", "width=900,height=1000");
  if (!win) {
    toast.error("Pop-up blocked — allow pop-ups to print.");
    return;
  }
  const style = `
    <style>
      body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 32px; color: #111; }
      h1 { font-size: 28px; margin: 0 0 4px; }
      .sub { color: #666; font-size: 13px; margin-bottom: 24px; }
      table { width: 100%; border-collapse: collapse; margin-top: 12px; }
      th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #ddd; font-size: 12px; }
      th { background: #f5f5f5; text-transform: uppercase; letter-spacing: 0.5px; font-size: 10px; }
      tr:nth-child(even) td { background: #fafafa; }
      td.num { text-align: right; font-family: "SF Mono", Menlo, monospace; }
      .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #ddd; color: #666; font-size: 11px; }
      @media print { body { padding: 16px; } }
    </style>`;
  const rowsHtml = tableRows
    .map(
      (r) =>
        `<tr>${r
          .map((c, i) => `<td class="${typeof c === "number" ? "num" : ""}">${
            typeof c === "number" ? fmt(c) : String(c ?? "")
          }</td>`)
          .join("")}</tr>`,
    )
    .join("");
  win.document.write(`<html><head><title>${title}</title>${style}</head><body>
    <h1>${title}</h1>
    <div class="sub">${subtitle}</div>
    <table><thead><tr>${tableHeaders.map((h) => `<th>${h}</th>`).join("")}</tr></thead>
      <tbody>${rowsHtml}</tbody></table>
    ${footer ? `<div class="footer">${footer}</div>` : ""}
  </body></html>`);
  win.document.close();
  // Give the browser a tick to render the layout before opening the print dialog.
  setTimeout(() => {
    try {
      win.focus();
      win.print();
    } catch (_) {
      /* user cancelled */
    }
  }, 250);
};

const MachineSpendDrillDialog = ({ open, machine, days, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !machine) return;
    setLoading(true);
    api
      .get(`/machines/${machine.machine_id}/services`)
      .then((r) => {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - Number(days || 90));
        const iso = cutoff.toISOString().slice(0, 10);
        setEntries((r.data || []).filter((e) => (e.serviced_at || "") >= iso));
      })
      .catch(() => toast.error("Could not load service history"))
      .finally(() => setLoading(false));
  }, [open, machine?.machine_id, days]);

  const totals = useMemo(() => {
    const t = {
      Service: 0,
      "Spare parts": 0,
      Consumables: 0,
      Repair: 0,
      "Call-out": 0,
      total: 0,
    };
    for (const e of entries) {
      const k = e.kind || "Service";
      const c = Number(e.cost || 0);
      t.total += c;
      if (k in t) t[k] += c;
      else t.Service += c;
    }
    return t;
  }, [entries]);

  if (!open || !machine) return null;

  const filename = `${machine.name.replace(/\W+/g, "-").toLowerCase()}-spend-${days}d`;

  const exportCsv = () => {
    const header = ["Date", "Kind", "Cost", "Notes"].join(",");
    const rows = entries.map((e) =>
      [
        csvEscape(e.serviced_at || ""),
        csvEscape(e.kind || "Service"),
        Number(e.cost || 0).toFixed(2),
        csvEscape((e.notes || "").replace(/\n/g, " ")),
      ].join(","),
    );
    downloadBlob([header, ...rows].join("\n"), `${filename}.csv`, "text/csv;charset=utf-8");
  };

  const exportPdf = () => {
    const rows = entries.map((e) => [
      e.serviced_at,
      e.kind || "Service",
      Number(e.cost || 0),
      (e.notes || "").replace(/\n/g, " · "),
    ]);
    rows.push(["", "TOTAL", totals.total, ""]);
    printReport(
      `Machine spend — ${machine.name}`,
      `Last ${days} days · ${entries.length} entr${entries.length === 1 ? "y" : "ies"} · Service $${fmt(totals.Service)} · Spare parts $${fmt(totals["Spare parts"])} · Consumables $${fmt(totals.Consumables)} · Repair $${fmt(totals.Repair)} · Call-out $${fmt(totals["Call-out"])}`,
      ["Date", "Kind", "Cost ($)", "Notes"],
      rows,
      `Generated ${new Date().toLocaleString()} · Podlab Inventory`,
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="machine-spend-drill-dialog"
      onClick={onClose}
    >
      <div
        className="nb-card w-[min(820px,95vw)] max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-start justify-between gap-4">
          <div>
            <div className="label-eyebrow">Machinery · spend breakdown</div>
            <h3 className="font-display text-2xl">{machine.name}</h3>
            <div className="label-eyebrow text-[#607087] mt-1">
              Last {days} days · {entries.length} entr
              {entries.length === 1 ? "y" : "ies"} · Total ${fmt(totals.total)}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              data-testid="drill-export-csv"
              onClick={exportCsv}
              disabled={!entries.length}
              className="nb-btn bg-white px-3 py-2 text-xs flex items-center gap-1 disabled:opacity-40"
            >
              <Download size={12} strokeWidth={2.5} /> CSV
            </button>
            <button
              data-testid="drill-export-pdf"
              onClick={exportPdf}
              disabled={!entries.length}
              className="nb-btn bg-white px-3 py-2 text-xs flex items-center gap-1 disabled:opacity-40"
            >
              <Printer size={12} strokeWidth={2.5} /> PDF
            </button>
            <button
              data-testid="drill-close"
              onClick={onClose}
              className="nb-btn bg-white p-2"
            >
              <X size={16} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        <div className="p-5 flex flex-col gap-4">
          {/* Kind subtotals row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {KINDS.map((k) => {
              const Icon = kindIcon(k);
              return (
                <div
                  key={k}
                  data-testid={`drill-subtotal-${k.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`nb-card-sm p-3 border-2 ${kindBadge(k)}`}
                >
                  <div className="flex items-center gap-2">
                    <Icon size={14} strokeWidth={2.5} />
                    <div className="label-eyebrow">{k}</div>
                  </div>
                  <div className="font-mono-num font-bold text-2xl mt-1">
                    ${fmt(totals[k])}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Entries list */}
          {loading ? (
            <div className="text-sm text-[#607087] py-8 text-center">
              Loading…
            </div>
          ) : entries.length === 0 ? (
            <div className="text-sm text-[#607087] py-8 text-center italic">
              No service entries in this period.
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {entries.map((e) => {
                const kind = e.kind || "Service";
                return (
                  <li
                    key={e.id}
                    data-testid={`drill-row-${e.id}`}
                    className="border-2 border-black rounded-xl p-3 bg-white flex gap-3"
                  >
                    <div className="font-mono-num font-bold text-sm shrink-0 w-24">
                      {e.serviced_at}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`inline-flex items-center gap-1 border-2 rounded-md px-2 py-0.5 text-xs font-semibold ${kindBadge(kind)}`}
                        >
                          {kind}
                        </span>
                        {e.cost != null && (
                          <span className="font-mono-num font-bold">
                            ${fmt(e.cost)}
                          </span>
                        )}
                      </div>
                      {e.notes && (
                        <div className="text-xs text-[#607087] mt-1 whitespace-pre-wrap">
                          {e.notes}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default MachineSpendDrillDialog;
